import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import { createGlobalStyle } from 'styled-components';
import type { FormRendererProps, FormRendererHandle } from './types/renderer';
import { useFormEngineSession } from './hooks/useFormEngineSession';
import { FormEngineProvider } from './context/FormEngineContext';
import type { FormEngineStateContextValue, FormEngineFormContextValue } from './context/FormEngineContext';
import { FormNodeRenderer } from './renderers/FormNodeRenderer';
import { FormRepeatableRenderer } from './renderers/FormRepeatableRenderer';
import { FormSubmitBar } from './renderers/FormSubmitBar';
import { FormStepperBar } from './renderers/FormStepperBar';
import { FormWizardStepper } from './renderers/FormWizardStepper';
import { FormReviewRenderer } from './renderers/FormReviewRenderer';
import { useRepeatableMode } from './processors/repeatableProcessor';
import { Page, Stack, Surface } from '@packages/ui/layout';
import { GoBack } from '@packages/ui/patterns/GoBack';
import { Typography, Spinner, Button } from '@packages/ui/primitives';
import { useConfirm } from '@packages/ui/composites';
import { useFormEventAdapter } from '@packages/block-events/react/adapters/useFormEventAdapter';
import { useEventScope } from '@packages/block-events/react/useEventScope';
import { diffValues } from '@packages/block-events/utils/diffValues';

/**
 * Scoped normalization for the `disabled-inputs` view presentation.
 * Ensures edge-case field types (RichText, FileInput, MaskedInput) that don't
 * natively respect disabled chrome still render with the same muted background.
 * All token-aligned primitives (TextInput, Select, DatePicker, Checkbox) apply
 * their own --ui-background-muted fill, so this is purely a safety net.
 */
const DisabledInputsGlobalStyle = createGlobalStyle`
  .form-engine--disabled-inputs {
    /* Catch any input/textarea inside the form that may have slipped through.
       Exclude radio/checkbox — their DS primitives use opacity:0 inputs with
       custom ::before/::after decorations that this override would cover. */
    input:disabled:not([type='radio']):not([type='checkbox']),
    textarea:disabled,
    select:disabled,
    button:disabled[data-ui-checkbox-select],
    [role='combobox'][aria-disabled='true'] {
      background-color: var(--ui-background-muted, #F3F3F3) !important;
      border-color: var(--ui-border) !important;
      color: var(--ui-text-subtle) !important;
      cursor: not-allowed;
      opacity: 1 !important;
      filter: none !important;
    }

    /* Addon wrappers (DOB, SSN, elect start/end, etc.) must not fade the field */
    [data-ui-input-group][data-disabled],
    [data-ui-input-group][data-disabled='true'] {
      opacity: 1 !important;
    }

    /* SearchPicker trigger */
    [data-ui-search-picker][data-disabled],
    [data-ui-search-picker] button:disabled {
      background-color: var(--ui-background-muted, #F3F3F3) !important;
      opacity: 1 !important;
    }

    /* File / upload dropzones */
    [data-ui-file-input][data-disabled],
    [data-ui-file-input][aria-disabled='true'] {
      background-color: var(--ui-background-muted, #F3F3F3) !important;
      opacity: 1 !important;
    }

    /* RichText editor in read-only mode: muted background */
    .ck-editor__editable[contenteditable='false'],
    .DraftEditor-root[aria-readonly='true'],
    .quill .ql-editor[contenteditable='false'],
    .ql-toolbar.ql-snow,
    .ql-container.ql-snow {
      background-color: var(--ui-background-muted, #F3F3F3) !important;
      opacity: 1 !important;
    }

    /* Signature canvas */
    canvas[aria-label] {
      opacity: 1 !important;
    }
  }
`;

function flattenErrorKeys(
  errors: Record<string, unknown>,
  prefix = '',
): string[] {
  const keys: string[] = [];
  for (const [k, v] of Object.entries(errors)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && 'message' in (v as Record<string, unknown>)) {
      keys.push(path);
    } else if (v && typeof v === 'object') {
      keys.push(...flattenErrorKeys(v as Record<string, unknown>, path));
    }
  }
  return keys;
}

export interface FormRendererExternalProps extends FormRendererProps {
  onCancel?: () => void;
  /**
   * Pass a `useFormRendererRef()` handle to enable external submit + state tracking.
   * Use `formHandle` (NOT `ref`) — React silently strips `ref` on plain function components.
   */
  formHandle?: FormRendererHandle;
}

export function FormRenderer(props: FormRendererExternalProps): React.ReactElement {
  const blockEventScope = useEventScope();

  // Block-events: refs for the adapter publish helpers so the merged callbacks
  // have stable identity (no re-render cascade through useFormEngineSession).
  const adapterRef = useRef<ReturnType<typeof useFormEventAdapter> | null>(null);
  const externalOnSubmitSuccessRef = useRef(props.onSubmitSuccess);
  externalOnSubmitSuccessRef.current = props.onSubmitSuccess;
  const externalOnSubmitFailureRef = useRef(props.onSubmitFailure);
  externalOnSubmitFailureRef.current = props.onSubmitFailure;

  const mergedOnSubmitSuccess = useCallback(
    (payload: { result: unknown; values: Record<string, unknown>; mode: string }) => {
      externalOnSubmitSuccessRef.current?.(payload);
      adapterRef.current?.publishSubmitSuccess(
        (payload.result as Record<string, unknown>) ?? {},
        payload.values,
        payload.mode as 'create' | 'edit',
      );
    },
    [],
  );

  const mergedOnSubmitFailure = useCallback(
    (payload: { error: unknown; mode: string }) => {
      externalOnSubmitFailureRef.current?.(payload);
      adapterRef.current?.publishSubmitFailure(
        [],
        payload.mode as 'create' | 'edit',
      );
    },
    [],
  );

  const session = useFormEngineSession({
    ...props,
    eventScope: props.eventScope ?? blockEventScope,
    onSubmitSuccess: mergedOnSubmitSuccess,
    onSubmitFailure: mergedOnSubmitFailure,
  });
  const { confirm } = useConfirm();

  const dirtyGuard = session.mode.lifecycle?.dirtyGuard;
  const isDirty = session.form.formState.isDirty;
  const isSubmitting = session.form.formState.isSubmitting;

  const effectiveIsDirty = isDirty;

  const sessionRef = useRef(session);
  sessionRef.current = session;
  const formAdapterHandle = useMemo(() => ({
    submitForm: () => sessionRef.current.onFormSubmitInternal(sessionRef.current.form.getValues()),
    getFormValues: () => sessionRef.current.form.getValues(),
    triggerValidation: () => sessionRef.current.form.trigger() as Promise<boolean>,
    get isDirty() { return sessionRef.current.form.formState.isDirty; },
    get isSubmitting() { return sessionRef.current.form.formState.isSubmitting; },
  }), []);
  const formAdapterOptions = useMemo(() => ({
    onExternalPatchValues: (values: Record<string, unknown>) => {
      const form = sessionRef.current.form;
      for (const [key, value] of Object.entries(values)) {
        form.setValue(key, value, { shouldDirty: true, shouldValidate: true });
      }
    },
    onExternalReset: (values?: Record<string, unknown>) => {
      sessionRef.current.form.reset(values);
    },
  }), []);
  const adapter = useFormEventAdapter(props.semanticId, formAdapterHandle, formAdapterOptions);
  adapterRef.current = adapter;

  const hydratingRef = useRef(true);
  useEffect(() => {
    if (session.status === 'idle' || session.status === 'hydrated') {
      hydratingRef.current = false;
    }
  }, [session.status]);

  useEffect(() => {
    if (!adapterRef.current?.publishValuesChanged || !session.form) return;
    let prevSnapshot: Record<string, unknown> = { ...session.form.getValues() };
    let timer: ReturnType<typeof setTimeout>;

    const sub = session.form.watch((allValues) => {
      if (hydratingRef.current) return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        const changedFields = diffValues(prevSnapshot, allValues as Record<string, unknown>);
        if (Object.keys(changedFields).length > 0) {
          adapterRef.current?.publishValuesChanged?.(changedFields, allValues as Record<string, unknown>);
          prevSnapshot = { ...(allValues as Record<string, unknown>) };
        }
      }, 200);
    });
    return () => { sub.unsubscribe(); clearTimeout(timer); };
  }, [session.form]);

  // Register form methods synchronously so the wizard can call them immediately
  // without waiting for a useEffect cycle. This ensures "Save & Continue"
  // works on the very first click even if the user is fast.
  if (props.formHandle?._registerFormMethods) {
    props.formHandle._registerFormMethods({
      trigger: () => new Promise<boolean>((resolve) => {
        session.form.handleSubmit(
          () => resolve(true),
          (errors) => {
            const firstKey = flattenErrorKeys(errors)[0];
            if (firstKey) {
              const el =
                document.querySelector(`[name="${firstKey}"]`) ??
                document.getElementById(firstKey);
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                requestAnimationFrame(() => {
                  if (el instanceof HTMLElement) el.focus({ preventScroll: true });
                });
              }
            }
            resolve(false);
          },
        )();
      }),
      getValues: () => session.form.getValues(),
      handleSubmit: () =>
        new Promise((resolve, reject) => {
          session.form.handleSubmit(
            async (values) => {
              try {
                const result = await session.onFormSubmitInternal(values);
                resolve(result);
              } catch (err) {
                reject(err);
              }
            },
            () => reject(new Error('VALIDATION_FAILED')),
          )();
        }),
    });
  }

  useEffect(() => {
    if (!dirtyGuard || !effectiveIsDirty) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dirtyGuard, effectiveIsDirty]);

  useEffect(() => {
    props.formHandle?._setState(isDirty, isSubmitting, session.status);
  }, [isDirty, isSubmitting, session.status, props.formHandle]);

  const handleCancel = useMemo(() => {
    if (!props.onCancel) return undefined;
    const onCancel = props.onCancel;
    return async () => {
      if (dirtyGuard && effectiveIsDirty) {
        const { confirmed } = await confirm({
          title: 'All data will be lost if you close. Are you sure you want to close?',
          variant: 'delete',
          actionLabel: 'Confirm',
          cancelLabel: 'Cancel',
          semanticId: 'dirtyGuardConfirm',
        });
        if (!confirmed) return;
      }
      onCancel();
    };
  }, [props.onCancel, dirtyGuard, effectiveIsDirty, confirm]);

  const hideRequiredMarker = Boolean((props.schema.metadata.page as Record<string, unknown> | undefined)?.hideRequiredMarker);

  const stateValue: FormEngineStateContextValue = useMemo(() => ({
    meta: session.meta,
    modeOverrides: session.modeOverrides,
    blockOverrides: session.blockOverrides,
    isReadOnly: session.isReadOnly,
    viewPresentation: session.viewPresentation,
    customRenderers: session.customRenderers,
    getOptionProp: session.getOptionProp,
    setOptionMeta: session.setOptionMeta,
    status: session.status,
    hideRequiredMarker,
    params: props.params,
    precisionConfig: props.schema.precision,
  }), [session.meta, session.modeOverrides, session.blockOverrides, session.isReadOnly, session.viewPresentation, session.customRenderers, session.getOptionProp, session.setOptionMeta, session.status, hideRequiredMarker, props.params, props.schema.precision]);

  const formValue: FormEngineFormContextValue = useMemo(() => ({
    form: session.form,
    udfRequiredFieldsRef: session.udfRequiredFieldsRef,
    hydratingRef: session.hydratingRef,
    hydrationEpochRef: session.hydrationEpochRef,
    programmaticWriteGuard: session.programmaticWriteGuardRef,
  }), [session.form, session.udfRequiredFieldsRef, session.hydratingRef, session.hydrationEpochRef, session.programmaticWriteGuardRef]);

  const hasSubmit = Boolean(session.mode.dataSource?.submit);
  const isLastStep = session.stepProcessor
    ? session.stepProcessor.currentStepIndex === session.stepProcessor.visibleSteps.length - 1
    : false;

  const layoutNodes = session.isWizard && session.stepLayout
    ? session.stepLayout
    : session.layout;

  const repeatable = useRepeatableMode({
    form: session.form as any,
    mode: session.mode,
    layout: layoutNodes,
  });

  const isFetching = session.status === 'fetching' && !props.skeletonOnFetch;
  const isFetchError = session.status === 'error';

  const wrapper = props.schema.features?.wrapper;

  const wizardStepper = session.isWizard && session.stepProcessor ? (
    <FormWizardStepper stepProcessor={session.stepProcessor} />
  ) : null;

  const isReviewStep = session.stepProcessor?.currentStep.type === 'review';

  const content = repeatable ? (
    <div style={{ paddingBottom: '20px' }}>
      <FormRepeatableRenderer
        repeatable={repeatable}
        layout={layoutNodes}
        effects={session.effects}
        observers={session.observers}
        precisionConfig={props.schema.precision}
      />
    </div>
  ) : isReviewStep && layoutNodes ? (
    <FormReviewRenderer layout={layoutNodes} />
  ) : (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0', paddingBottom: '20px' }}>
      {layoutNodes.map((node, idx) => (
        <FormNodeRenderer key={idx} node={node} />
      ))}
    </div>
  );

  const baseSubmitBarConfig = props.schema.features?.submitBar;
  const modeSubmitLabel = props.schema.metadata.modeSubmitLabels?.[session.modeName];
  const isInsideBorderedWrapper = Boolean(wrapper?.bordered !== false && wrapper != null);
  const effectiveSubmitBarConfig = {
    ...(isInsideBorderedWrapper && { noHorizontalPadding: true }),
    ...baseSubmitBarConfig,
    ...(modeSubmitLabel != null && { submitLabel: modeSubmitLabel }),
  };

  const hideCancel = effectiveSubmitBarConfig?.hideCancel ?? false;
  const cancelAction = handleCancel && !hideCancel ? [{ label: 'Cancel', onClick: handleCancel }] : undefined;
  const mergedActions = cancelAction
    ? [...(cancelAction), ...(props.actions ?? [])]
    : props.actions;

  const submitBar = session.isWizard && session.stepProcessor ? (
    <FormStepperBar
      stepProcessor={session.stepProcessor}
      isLastStep={isLastStep}
      hasSubmit={hasSubmit}
    />
  ) : (
    <FormSubmitBar
      modeName={session.modeName}
      actions={mergedActions}
      hasSubmit={hasSubmit}
      config={effectiveSubmitBarConfig}
    />
  );

  const { metadata } = props.schema;
  const pageConfig = metadata.page;
  const renderPageShell = Boolean(pageConfig && props.onCancel && !props.hideTitle);

  const showSubmitBar = !props.formHandle?._hideSubmitBar;
  const submitBarPosition = props.schema.features?.submitBar?.position ?? 'bottom';
  const showTopBar = showSubmitBar && (submitBarPosition === 'top' || submitBarPosition === 'both');
  const showBottomBar = showSubmitBar && (submitBarPosition === 'bottom' || submitBarPosition === 'both');

  const formBody = isFetchError ? (
    <div style={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '300px', flexDirection: 'column', gap: '8px' }}>
      <span className='material-symbols-outlined' style={{ fontSize: '48px', color: '#c0392b' }}>error</span>
      <span style={{ color: '#c0392b', fontSize: '14px' }}>Failed to load data. Please close and try again.</span>
    </div>
  ) : isFetching ? (
    <div style={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '300px' }}>
      <Spinner size='lg' />
    </div>
  ) : (
    <>
      {session.formHistory?.hasDraft && (
        <Surface bordered padding='12px 16px' tone='info' style={{ marginBottom: '16px' }}>
          <Stack gap='8px'>
            <Typography variant='body-s'>
              A saved draft was found. Would you like to restore it?
            </Typography>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                variant='primary-outline'
                size='sm'
                label='Restore draft'
                type='button'
                onClick={() => session.formHistory?.restoreDraft()}
              />
              <Button
                variant='ghost'
                size='sm'
                label='Dismiss'
                type='button'
                onClick={() => session.formHistory?.dismissDraft()}
              />
            </div>
          </Stack>
        </Surface>
      )}
      {repeatable ? (
        <>
          {showTopBar && submitBar}
          {content}
          {showBottomBar && submitBar}
        </>
      ) : wrapper ? (
        <>
          {wizardStepper}
          {wrapper.bordered === false ? (
            <div style={wrapper.padding != null && wrapper.padding !== '0' ? { padding: wrapper.padding } : undefined}>
              {showTopBar && submitBar}
              {content}
              {showBottomBar && submitBar}
            </div>
          ) : (
            <Surface bordered={wrapper.bordered} padding={wrapper.padding ?? '28px 24px 0px 24px'}>
              {showTopBar && submitBar}
              {content}
              {showBottomBar && submitBar}
            </Surface>
          )}
        </>
      ) : (
        <>
          {wizardStepper}
          {showTopBar && submitBar}
          {content}
          {showBottomBar && submitBar}
        </>
      )}
    </>
  );

  const viewModeClass =
    session.isReadOnly && session.viewPresentation === 'disabled-inputs'
      ? 'form-engine--disabled-inputs'
      : undefined;

  const formElement = (
    <FormEngineProvider stateValue={stateValue} formValue={formValue}>
      {viewModeClass && React.createElement(DisabledInputsGlobalStyle as unknown as React.ComponentType)}
      <form
        id={props.formHandle?.formId}
        onSubmit={session.onFormSubmit}
        className={[props.className, viewModeClass].filter(Boolean).join(' ') || undefined}
        noValidate
        style={isFetching ? { display: 'flex', flexDirection: 'column', flex: 1, height: '100%' } : undefined}
      >
        {formBody}
      </form>
    </FormEngineProvider>
  );

  if (!renderPageShell) return formElement;

  const showGoBack = pageConfig!.goBack !== false;
  const resolvedTitle = metadata.modeTitles?.[session.modeName] ?? metadata.title;

  return (
    <Page semanticId={metadata.semanticId}>
      {showGoBack && handleCancel && <GoBack onClick={handleCancel} label={pageConfig!.goBackLabel} />}
      <Typography variant='h3' style={{ marginBottom: '16px' }}>{resolvedTitle}</Typography>
      {formElement}
    </Page>
  );
}
